import { Campaign as CampaignIcon, Close, FilterAlt, ForwardToInbox, Pause, PlayArrow, Schedule, Send, Undo } from '@mui/icons-material'
import {
  Alert,
  alpha,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material'
import type { Contact } from '@otw/models/leads/contacts'
import type { ContactCompany } from '@otw/models/leads/contactCompanies'
import {
  CAMPAIGN_REPEAT_LABEL,
  CAMPAIGN_REPEATS,
  CAMPAIGN_STATUS_LABEL,
  MARKETING_CAMPAIGN_FROM_EMAIL,
  MARKETING_CAMPAIGN_FROM_NAME,
  MARKETING_PERSONALIZATION_TOKENS,
  buildAudienceEvalContext,
  cloneAudienceFilterGroup,
  emptyAudienceFilter,
  parseAudienceFilterGroup,
  parseCampaignRepeat,
  parseReplyToUserIds,
  previewAudienceEligibility,
  type AudienceFilterGroup,
  type CampaignRepeat,
  type MarketingCampaign,
} from '@otw/models/leads/marketingCampaign'
import type { Terminal } from '@otw/models/leads/terminals'
import type { PickupLocation, PrivateUserInfoType } from '@otw/models'
import { DateTimePicker, LocalizationProvider } from '@mui/x-date-pickers'
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs'
import dayjs, { Dayjs } from 'dayjs'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import type { Vehicle } from '@otw/models'
import {
  campaignFeaturedVehicleFromRecord,
  campaignFeaturedVehiclesHtml,
  defaultCampaignFeaturedVehicleLayout,
  parseCampaignFeaturedVehicleLayout,
  type CampaignFeaturedVehicleLayout,
} from '@otw/models/leads/campaignFeaturedVehicles'
import {
  resolveCampaignVehiclePhotoUrl,
  useCampaignInventoryVehicles,
} from 'hooks/inventory/useCampaignInventoryVehicles'
import { useAuth } from 'contexts/AuthContext'
import { useMarketingAudiences } from 'hooks/contacts/useMarketingAudiences'
import { useMarketingCampaigns } from 'hooks/contacts/useMarketingCampaigns'
import { getUserDisplayName } from 'hooks/useUsers'
import AudienceEligibilitySummary from '../campaigns/AudienceEligibilitySummary'
import AudienceFilterBuilder from '../campaigns/AudienceFilterBuilder'
import { formatNumber } from '../../util/customerUtils'

interface CampaignFormDialogProps {
  open: boolean
  onClose: () => void
  campaign: MarketingCampaign | null
  contacts: Contact[]
  companies: ContactCompany[]
  pickupLocations: PickupLocation[]
  terminals: Terminal[]
  salesOwners: PrivateUserInfoType[]
  inventoryOwners: PrivateUserInfoType[]
}

const CUSTOM_AUDIENCE = '__custom__'

const INSERTABLE_TOKENS = MARKETING_PERSONALIZATION_TOKENS.filter(
  (item) => item.token !== 'UnsubscribeUrl',
)

const contactOptionLabel = (contact: Contact): string => {
  const name = contact.name.trim() || 'Unnamed contact'
  const email = contact.email?.trim()
  return email ? `${name} · ${email}` : name
}

const vehicleOptionLabel = (vehicle: Vehicle): string => {
  const title = [vehicle.year, vehicle.make, vehicle.model].filter(Boolean).join(' ')
  const stock = vehicle.stock_number?.trim()
  const size = vehicle.size?.trim()
  const bits = [title, size, stock ? `Stock ${stock}` : ''].filter(Boolean)
  return bits.join(' · ') || vehicle.id
}

const contactsByIds = (contacts: Contact[], ids: string[]): Contact[] => {
  const byId = new Map(contacts.map((contact) => [contact.id, contact]))
  return ids.flatMap((id) => {
    const contact = byId.get(id)
    return contact ? [contact] : []
  })
}

const looksLikeHtml = (value: string): boolean => /<[a-z][\s\S]*>/i.test(value.trim())

const CampaignFormDialog: React.FC<CampaignFormDialogProps> = ({
  open,
  onClose,
  campaign,
  contacts,
  companies,
  pickupLocations,
  terminals,
  salesOwners,
  inventoryOwners,
}) => {
  const theme = useTheme()
  const accent = theme.palette.primary.main
  const { userInfo } = useAuth()
  const {
    createCampaign,
    updateCampaign,
    sendCampaign,
    sendTestCampaign,
    scheduleCampaign,
    pauseCampaign,
    resumeCampaign,
    cancelCampaign,
    returnCampaignToDraft,
    isSaving,
    isSending,
    isSendingTest,
    isUpdatingStatus,
  } = useMarketingCampaigns()
  const { audiences, createAudience, isSaving: isSavingAudience } = useMarketingAudiences()
  const [name, setName] = useState('')
  const [replyToUsers, setReplyToUsers] = useState<PrivateUserInfoType[]>([])
  const [subject, setSubject] = useState('')
  const [htmlBody, setHtmlBody] = useState('')
  const [audienceFilter, setAudienceFilter] = useState<AudienceFilterGroup>(
    emptyAudienceFilter(),
  )
  const [savedAudienceId, setSavedAudienceId] = useState('')
  const [saveAudienceName, setSaveAudienceName] = useState('')
  const [confirmSendOpen, setConfirmSendOpen] = useState(false)
  const [confirmScheduleOpen, setConfirmScheduleOpen] = useState(false)
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false)
  const [confirmTestOpen, setConfirmTestOpen] = useState(false)
  const [workingCampaignId, setWorkingCampaignId] = useState<string | null>(null)
  const [scheduledAt, setScheduledAt] = useState<Dayjs | null>(null)
  const [repeat, setRepeat] = useState<CampaignRepeat>('none')
  const [includedContactIds, setIncludedContactIds] = useState<string[]>([])
  const [excludedContactIds, setExcludedContactIds] = useState<string[]>([])
  const [featuredVehicleIds, setFeaturedVehicleIds] = useState<string[]>([])
  const [featuredVehicleLayout, setFeaturedVehicleLayout] =
    useState<CampaignFeaturedVehicleLayout>('cards')
  const [featuredVehicleLayoutTouched, setFeaturedVehicleLayoutTouched] = useState(false)
  const [photoUrlByVehicleId, setPhotoUrlByVehicleId] = useState<Record<string, string>>({})
  const bodyRef = useRef<HTMLTextAreaElement | null>(null)

  const { vehicles: inventoryVehicles, isLoading: isLoadingInventory } =
    useCampaignInventoryVehicles(open)
  const effectiveVehicleLayout = featuredVehicleLayoutTouched
    ? featuredVehicleLayout
    : defaultCampaignFeaturedVehicleLayout(featuredVehicleIds.length)
  const featuredPreviewHtml = useMemo(() => {
    const byId = new Map(inventoryVehicles.map((vehicle) => [vehicle.id, vehicle]))
    const selected = featuredVehicleIds.flatMap((id) => {
      const vehicle = byId.get(id)
      if (!vehicle) return []
      const mapped = campaignFeaturedVehicleFromRecord(id, {
        year: vehicle.year,
        make: vehicle.make,
        model: vehicle.model,
        size: vehicle.size,
        stock_number: vehicle.stock_number,
        miles: vehicle.miles,
        optic_list_price: vehicle.optic_list_price,
        show_on_website: vehicle.show_on_website,
        photo_url: photoUrlByVehicleId[id],
      })
      return mapped ? [mapped] : []
    })
    return campaignFeaturedVehiclesHtml(selected, effectiveVehicleLayout)
  }, [effectiveVehicleLayout, featuredVehicleIds, inventoryVehicles, photoUrlByVehicleId])

  const replyToOptions = useMemo(
    () =>
      salesOwners
        .filter((user) => Boolean(user.email?.trim()))
        .slice()
        .sort((a, b) => getUserDisplayName(a).localeCompare(getUserDisplayName(b), 'en')),
    [salesOwners],
  )

  useEffect(() => {
    if (!open || featuredVehicleIds.length === 0) {
      setPhotoUrlByVehicleId({})
      return
    }
    const byId = new Map(inventoryVehicles.map((vehicle) => [vehicle.id, vehicle]))
    let cancelled = false
    void Promise.all(
      featuredVehicleIds.map(async (id) => {
        const vehicle = byId.get(id)
        if (!vehicle) return [id, ''] as const
        const url = await resolveCampaignVehiclePhotoUrl(vehicle)
        return [id, url ?? ''] as const
      }),
    ).then((entries) => {
      if (cancelled) return
      const next: Record<string, string> = {}
      for (const [id, url] of entries) {
        if (url) next[id] = url
      }
      setPhotoUrlByVehicleId(next)
    })
    return () => {
      cancelled = true
    }
  }, [featuredVehicleIds, inventoryVehicles, open])

  useEffect(() => {
    if (!open) return
    if (campaign) {
      setName(campaign.name)
      setSubject(campaign.subject)
      setHtmlBody(campaign.htmlBody)
      setAudienceFilter(parseAudienceFilterGroup(campaign.audienceFilter))
      setSavedAudienceId(campaign.savedAudienceId?.trim() ?? '')
      setSaveAudienceName('')
      setConfirmSendOpen(false)
      setConfirmScheduleOpen(false)
      setConfirmCancelOpen(false)
      setConfirmTestOpen(false)
      setWorkingCampaignId(campaign.id)
      setScheduledAt(campaign.scheduledAt ? dayjs(campaign.scheduledAt) : null)
      setRepeat(parseCampaignRepeat(campaign.repeat))
      setIncludedContactIds(parseReplyToUserIds(campaign.includedContactIds))
      setExcludedContactIds(parseReplyToUserIds(campaign.excludedContactIds))
      const nextVehicleIds = parseReplyToUserIds(campaign.featuredVehicleIds)
      const storedLayout = parseCampaignFeaturedVehicleLayout(campaign.featuredVehicleLayout)
      setFeaturedVehicleIds(nextVehicleIds)
      setFeaturedVehicleLayout(
        storedLayout ?? defaultCampaignFeaturedVehicleLayout(nextVehicleIds.length),
      )
      setFeaturedVehicleLayoutTouched(Boolean(storedLayout))
    } else {
      setName('')
      setReplyToUsers([])
      setSubject('')
      setHtmlBody('')
      setAudienceFilter(emptyAudienceFilter())
      setSavedAudienceId('')
      setSaveAudienceName('')
      setConfirmSendOpen(false)
      setConfirmScheduleOpen(false)
      setConfirmCancelOpen(false)
      setConfirmTestOpen(false)
      setWorkingCampaignId(null)
      setScheduledAt(null)
      setRepeat('none')
      setIncludedContactIds([])
      setExcludedContactIds([])
      setFeaturedVehicleIds([])
      setFeaturedVehicleLayout('cards')
      setFeaturedVehicleLayoutTouched(false)
    }
  }, [open, campaign])

  useEffect(() => {
    if (!open || !campaign) return
    const selectedIds = new Set(parseReplyToUserIds(campaign.replyToUserIds))
    setReplyToUsers(replyToOptions.filter((user) => selectedIds.has(String(user.id))))
  }, [open, campaign, replyToOptions])

  useEffect(() => {
    if (!open || !savedAudienceId || audiences.length === 0) return
    if (!audiences.some((audience) => audience.id === savedAudienceId)) {
      setSavedAudienceId('')
    }
  }, [open, audiences, savedAudienceId])

  const evalContext = useMemo(
    () => buildAudienceEvalContext({ companies, pickupLocations }),
    [companies, pickupLocations],
  )

  const eligibility = useMemo(
    () =>
      previewAudienceEligibility(contacts, audienceFilter, evalContext, {
        includedContactIds,
        excludedContactIds,
      }),
    [audienceFilter, contacts, evalContext, excludedContactIds, includedContactIds],
  )

  const canSave =
    name.trim().length > 0 && !isSaving && !isSending && !isSendingTest && !isUpdatingStatus
  const isDraft = !campaign || campaign.status === 'draft'
  const isScheduled = campaign?.status === 'scheduled'
  const isPaused = campaign?.status === 'paused'
  const isCancelled = campaign?.status === 'cancelled'
  const canContinueSend = campaign?.status === 'sending'
  const canReturnToDraft =
    Boolean(campaign) &&
    (isScheduled || (isPaused && (campaign?.sentCount ?? 0) === 0))
  const linkedToSaved = Boolean(savedAudienceId)
  const busy =
    isSaving || isSavingAudience || isSending || isSendingTest || isUpdatingStatus
  const testToEmail = userInfo?.email?.trim() || ''
  const canSendTest =
    !busy && name.trim().length > 0 && subject.trim().length > 0 && Boolean(testToEmail)
  const scheduleIsFuture =
    Boolean(scheduledAt?.isValid()) && (scheduledAt?.valueOf() ?? 0) > Date.now()

  const persistDraft = async (): Promise<string> => {
    const replyToUserIds = replyToUsers.map((user) => String(user.id))
    const nextScheduledAt =
      scheduledAt?.isValid() ? scheduledAt.toDate() : null
    const existingId = campaign?.id || workingCampaignId
    if (existingId) {
      await updateCampaign({
        id: existingId,
        name: name.trim(),
        subject: subject.trim(),
        htmlBody,
        audienceFilter,
        savedAudienceId,
        replyToUserIds,
        includedContactIds,
        excludedContactIds,
        featuredVehicleIds,
        ...(featuredVehicleLayoutTouched ? { featuredVehicleLayout } : {}),
        scheduledAt: nextScheduledAt,
        repeat,
      })
      return existingId
    }
    const createdId = await createCampaign({
      name: name.trim(),
      subject: subject.trim(),
      htmlBody,
      audienceFilter,
      savedAudienceId,
      replyToUserIds,
      includedContactIds,
      excludedContactIds,
      featuredVehicleIds,
      ...(featuredVehicleLayoutTouched ? { featuredVehicleLayout } : {}),
      scheduledAt: nextScheduledAt,
      repeat,
    })
    setWorkingCampaignId(createdId)
    return createdId
  }

  const applySavedAudience = (audienceId: string) => {
    if (audienceId === CUSTOM_AUDIENCE || !audienceId) {
      setSavedAudienceId('')
      return
    }
    const audience = audiences.find((item) => item.id === audienceId)
    if (!audience) {
      setSavedAudienceId('')
      return
    }
    setSavedAudienceId(audience.id)
    setAudienceFilter(cloneAudienceFilterGroup(parseAudienceFilterGroup(audience.filter)))
    setIncludedContactIds(parseReplyToUserIds(audience.includedContactIds))
    setExcludedContactIds(parseReplyToUserIds(audience.excludedContactIds))
  }

  const handleCustomize = () => {
    setSavedAudienceId('')
  }

  const handleSaveAsAudience = async () => {
    const trimmed = saveAudienceName.trim()
    if (!trimmed) return
    await createAudience({
      name: trimmed,
      filter: audienceFilter,
      includedContactIds,
      excludedContactIds,
    })
    setSaveAudienceName('')
  }

  const handleSave = async () => {
    if (!canSave || !isDraft) return
    await persistDraft()
    onClose()
  }

  const canSendNow =
    !busy &&
    name.trim().length > 0 &&
    subject.trim().length > 0 &&
    (canContinueSend ||
      ((isDraft || isScheduled) && eligibility.eligibleCount > 0))

  const canSchedule =
    !busy &&
    (isDraft || isScheduled) &&
    name.trim().length > 0 &&
    subject.trim().length > 0 &&
    (repeat !== 'none' || eligibility.eligibleCount > 0) &&
    scheduleIsFuture

  const handleSendNow = async () => {
    if (!canSendNow) return
    const campaignId = isDraft ? await persistDraft() : campaign!.id
    await sendCampaign(campaignId)
    setConfirmSendOpen(false)
    onClose()
  }

  const handleSendTest = async () => {
    if (!canSendTest) return
    const campaignId = isDraft ? await persistDraft() : campaign!.id
    await sendTestCampaign(campaignId)
    setConfirmTestOpen(false)
  }

  const handleSchedule = async () => {
    if (!canSchedule || !scheduledAt) return
    const campaignId = isDraft ? await persistDraft() : campaign!.id
    await scheduleCampaign({
      id: campaignId,
      scheduledAt: scheduledAt.toDate(),
      repeat,
    })
    setConfirmScheduleOpen(false)
    onClose()
  }

  const handlePause = async () => {
    if (!campaign) return
    await pauseCampaign(campaign.id)
    onClose()
  }

  const handleResume = async () => {
    if (!campaign) return
    await resumeCampaign(campaign.id)
    onClose()
  }

  const handleReturnToDraft = async () => {
    if (!campaign) return
    await returnCampaignToDraft(campaign.id)
    onClose()
  }

  const handleCancelCampaign = async () => {
    if (!campaign) return
    await cancelCampaign(campaign.id)
    setConfirmCancelOpen(false)
    onClose()
  }

  const insertToken = (token: string) => {
    const snippet = `{{${token}}}`
    const field = bodyRef.current
    if (!field) {
      setHtmlBody((current) => current + snippet)
      return
    }
    const start = field.selectionStart ?? htmlBody.length
    const end = field.selectionEnd ?? start
    setHtmlBody((current) => current.slice(0, start) + snippet + current.slice(end))
    const caret = start + snippet.length
    window.requestAnimationFrame(() => {
      field.focus()
      field.setSelectionRange(caret, caret)
    })
  }

  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          borderRadius: { xs: 0, sm: 3 },
          maxHeight: { xs: '100vh', sm: '94vh' },
        },
      }}
    >
      <DialogTitle
        sx={{
          p: { xs: 2, md: 2.5 },
          background: `linear-gradient(135deg, ${alpha(accent, 0.12)} 0%, ${alpha(accent, 0.04)} 100%)`,
          borderBottom: `1px solid ${alpha(accent, 0.18)}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Avatar
            variant="rounded"
            sx={{ bgcolor: alpha(accent, 0.18), color: accent, width: 44, height: 44 }}
          >
            <CampaignIcon />
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <Typography
                variant="overline"
                sx={{ color: accent, letterSpacing: 1.2, fontWeight: 800, fontSize: '1rem' }}
              >
                {campaign ? 'Edit campaign' : 'New campaign'}
              </Typography>
              <Tooltip title="SMS and other types are reserved for later.">
                <Chip size="small" label="Email" variant="outlined" />
              </Tooltip>
              {campaign ? (
                <Chip
                  size="small"
                  label={CAMPAIGN_STATUS_LABEL[campaign.status]}
                  color={
                    campaign.status === 'sent'
                      ? 'success'
                      : campaign.status === 'scheduled'
                        ? 'info'
                        : campaign.status === 'paused' || campaign.status === 'sending'
                          ? 'warning'
                          : campaign.status === 'cancelled'
                            ? 'error'
                            : 'default'
                  }
                />
              ) : null}
            </Stack>
            <Typography
              variant="caption"
              color="text.primary"
              sx={{ display: 'block', lineHeight: 1.4 }}
            >
              {formatNumber(eligibility.eligibleCount)} eligible of{' '}
              {formatNumber(eligibility.matchingCount)} matching contacts right now.
            </Typography>
          </Box>
          <IconButton
            onClick={onClose}
            size="small"
            disabled={busy}
            sx={{
              color: 'text.secondary',
              '&:hover': { bgcolor: alpha(accent, 0.08), color: accent },
            }}
          >
            <Close />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ p: { xs: 1.5, md: 3 } }} dividers={false}>
        <Stack spacing={2.5} sx={{ pt: { xs: 2, md: 2.5 } }}>
          {campaign?.status === 'sent' ? (
            <Alert severity="info">
              This campaign has already been sent. Duplicate it to create a new draft.
            </Alert>
          ) : null}
          {isScheduled ? (
            <Alert severity="info">
              Scheduled
              {campaign?.scheduledAt
                ? ` for ${campaign.scheduledAt.toLocaleString('en-US')}`
                : ''}
              . Send now emails immediately instead of waiting. Return to draft to edit the
              content.
            </Alert>
          ) : null}
          {isPaused ? (
            <Alert severity="warning">
              This campaign is paused. Resume to continue, or cancel it.
            </Alert>
          ) : null}
          {isCancelled ? (
            <Alert severity="error">
              This campaign is cancelled and will not send. Duplicate it to start over.
            </Alert>
          ) : null}
          {canContinueSend ? (
            <Alert severity="warning">
              Send was interrupted. Continue sending to finish remaining recipients, or pause
              it.
            </Alert>
          ) : null}

          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Details
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Campaign name"
                value={name}
                onChange={(event) => setName(event.target.value)}
                required
                fullWidth
                disabled={!isDraft || busy}
                helperText="Internal only. Recipients never see this."
              />
              <TextField
                label="From"
                value={`${MARKETING_CAMPAIGN_FROM_NAME} <${MARKETING_CAMPAIGN_FROM_EMAIL}>`}
                fullWidth
                disabled
                helperText="Locked to the portal’s usual send address until a sales mailbox exists."
              />
              <Autocomplete
                multiple
                disableCloseOnSelect
                options={replyToOptions}
                value={replyToUsers}
                onChange={(_, value) => setReplyToUsers(value)}
                disabled={!isDraft || busy}
                getOptionLabel={(option) => {
                  const name = getUserDisplayName(option)
                  const email = option.email?.trim() ?? ''
                  return email ? `${name} (${email})` : name
                }}
                isOptionEqualToValue={(opt, val) => String(opt.id) === String(val.id)}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => (
                    <Chip
                      {...getTagProps({ index })}
                      key={String(option.id)}
                      size="small"
                      label={getUserDisplayName(option)}
                    />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Reply to"
                    placeholder={replyToUsers.length === 0 ? 'Sales team and owners' : ''}
                    helperText="Replies go to these people. Leave empty and replies go to admin@optictruckworks.com."
                  />
                )}
              />
              {(isDraft || isScheduled) ? (
                <Stack spacing={2}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DateTimePicker
                      label="Schedule send"
                      value={scheduledAt}
                      onChange={(value) => setScheduledAt(value)}
                      disabled={busy}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          helperText: isScheduled
                            ? 'Change the time, then Schedule to update it.'
                            : 'Saved with the draft. Use Schedule to actually queue the send.',
                        },
                      }}
                    />
                  </LocalizationProvider>
                  <FormControl fullWidth disabled={busy}>
                    <InputLabel id="campaign-repeat-label">Repeat</InputLabel>
                    <Select
                      labelId="campaign-repeat-label"
                      label="Repeat"
                      value={repeat}
                      onChange={(event) =>
                        setRepeat(parseCampaignRepeat(event.target.value))
                      }
                    >
                      {CAMPAIGN_REPEATS.map((item) => (
                        <MenuItem key={item} value={item}>
                          {CAMPAIGN_REPEAT_LABEL[item]}
                        </MenuItem>
                      ))}
                    </Select>
                    <FormHelperText>
                      After each send, the next date is queued. Missed slots are skipped, not
                      backfilled.
                    </FormHelperText>
                  </FormControl>
                </Stack>
              ) : campaign?.scheduledAt ? (
                <TextField
                  label="Scheduled send"
                  value={`${campaign.scheduledAt.toLocaleString('en-US')}${
                    parseCampaignRepeat(campaign.repeat) !== 'none'
                      ? ` · ${CAMPAIGN_REPEAT_LABEL[parseCampaignRepeat(campaign.repeat)]}`
                      : ''
                  }`}
                  fullWidth
                  disabled
                />
              ) : parseCampaignRepeat(campaign?.repeat) !== 'none' ? (
                <TextField
                  label="Repeat"
                  value={CAMPAIGN_REPEAT_LABEL[parseCampaignRepeat(campaign?.repeat)]}
                  fullWidth
                  disabled
                />
              ) : null}
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1.5 }}>
              Content
            </Typography>
            <Stack spacing={2}>
              <TextField
                label="Subject"
                value={subject}
                onChange={(event) => setSubject(event.target.value)}
                fullWidth
                disabled={!isDraft || busy}
              />
              <TextField
                label="Email content"
                value={htmlBody}
                onChange={(event) => setHtmlBody(event.target.value)}
                inputRef={bodyRef}
                fullWidth
                multiline
                minRows={10}
                disabled={!isDraft || busy}
                helperText="This is the message inside the Optic Truck Works email (logo header and unsubscribe footer). Plain text or HTML is fine."
              />
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Click to insert a personalization token at your cursor
                </Typography>
                <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.75 }}>
                  {INSERTABLE_TOKENS.map((item) => (
                    <Tooltip key={item.token} title={item.label}>
                      <Chip
                        size="small"
                        label={`{{${item.token}}}`}
                        onClick={() => insertToken(item.token)}
                        disabled={!isDraft || busy}
                        sx={{ fontFamily: 'monospace' }}
                      />
                    </Tooltip>
                  ))}
                </Stack>
              </Box>
              <Box>
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Trucks in this email
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    size="small"
                    disabled={!isDraft || busy || featuredVehicleIds.length === 0}
                    value={effectiveVehicleLayout}
                    onChange={(_, next: CampaignFeaturedVehicleLayout | null) => {
                      if (next !== 'cards' && next !== 'list') return
                      setFeaturedVehicleLayoutTouched(true)
                      setFeaturedVehicleLayout(next)
                    }}
                  >
                    <ToggleButton value="cards">Highlight cards</ToggleButton>
                    <ToggleButton value="list">List</ToggleButton>
                  </ToggleButtonGroup>
                </Stack>
                <Autocomplete
                  multiple
                  disabled={!isDraft || busy}
                  loading={isLoadingInventory}
                  options={inventoryVehicles}
                  value={inventoryVehicles.filter((vehicle) => featuredVehicleIds.includes(vehicle.id))}
                  onChange={(_, next) => setFeaturedVehicleIds(next.map((vehicle) => vehicle.id))}
                  getOptionLabel={vehicleOptionLabel}
                  isOptionEqualToValue={(left, right) => left.id === right.id}
                  renderTags={(value, getTagProps) =>
                    value.map((option, index) => {
                      const { key, ...tagProps } = getTagProps({ index })
                      return (
                        <Chip key={key} {...tagProps} size="small" label={vehicleOptionLabel(option)} />
                      )
                    })
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Inventory trucks"
                      placeholder="Search year, make, model, or stock"
                    />
                  )}
                />
                <FormHelperText>
                  Any inventory truck can be included. Defaults to highlight cards for 1–3 trucks and a
                  list for 4 or more until you pick a layout. Cards sit three across and include a
                  photo when the truck has one. VIN is not included. Trucks that are not on the
                  website are shown without a public link.
                </FormHelperText>
              </Box>
              <Box
                sx={{
                  bgcolor: '#f5f5f5',
                  borderRadius: 2,
                  p: 2,
                }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  Preview — how the email looks when sent
                </Typography>
                <Paper
                  elevation={0}
                  sx={{
                    maxWidth: 480,
                    mx: 'auto',
                    borderRadius: 2,
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <Box sx={{ p: 2.5, textAlign: 'center', borderBottom: '1px solid #e0e0e0' }}>
                    <Box
                      component="img"
                      src="https://firebasestorage.googleapis.com/v0/b/otw-production-91ffb.appspot.com/o/public%2FLogoVariation-OpticTruckWorks-05.png?alt=media&token=c3428c05-276c-4d32-ac33-a7d9d51fe380"
                      alt="Optic Truck Works"
                      sx={{ maxWidth: 160, height: 'auto' }}
                    />
                  </Box>
                  <Box sx={{ p: 2.5 }}>
                    {htmlBody.trim() ? (
                      looksLikeHtml(htmlBody) ? (
                        <Box
                          sx={{ fontSize: 16, color: '#333', lineHeight: 1.6 }}
                          dangerouslySetInnerHTML={{ __html: htmlBody }}
                        />
                      ) : (
                        <Typography
                          sx={{ fontSize: 16, color: '#333', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}
                        >
                          {htmlBody}
                        </Typography>
                      )
                    ) : (
                      <Typography variant="body2" color="text.disabled">
                        Email content appears here
                      </Typography>
                    )}
                    {featuredPreviewHtml ? (
                      <Box dangerouslySetInnerHTML={{ __html: featuredPreviewHtml }} />
                    ) : null}
                    <Typography
                      sx={{
                        fontSize: 16,
                        color: '#666',
                        lineHeight: 1.6,
                        borderTop: '1px solid #e0e0e0',
                        pt: 3,
                        mt: 4,
                      }}
                    >
                      Best regards,
                      <br />
                      The Optic Truck Works Team
                    </Typography>
                    <Typography sx={{ fontSize: 12, color: '#666', mt: 2 }}>
                      You received this email because you are in the Optic Truck Works CRM.{' '}
                      Unsubscribe from marketing emails.
                    </Typography>
                  </Box>
                </Paper>
              </Box>
            </Stack>
          </Paper>

          <Paper variant="outlined" sx={{ p: { xs: 1.5, md: 2 }, borderRadius: 3 }}>
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              justifyContent="space-between"
              sx={{ mb: 1.5 }}
            >
              <Typography variant="subtitle2" fontWeight={700}>
                Audience
              </Typography>
              {linkedToSaved ? (
                <Chip
                  size="small"
                  icon={<FilterAlt fontSize="small" />}
                  label="Using a saved audience"
                  color="info"
                  variant="outlined"
                />
              ) : null}
            </Stack>
            <Stack spacing={2}>
              <FormControl fullWidth disabled={!isDraft || busy}>
                <InputLabel id="campaign-saved-audience-label">Saved audience</InputLabel>
                <Select
                  labelId="campaign-saved-audience-label"
                  label="Saved audience"
                  value={savedAudienceId || CUSTOM_AUDIENCE}
                  onChange={(event) => applySavedAudience(String(event.target.value))}
                >
                  <MenuItem value={CUSTOM_AUDIENCE}>Custom filters</MenuItem>
                  {audiences.map((audience) => (
                    <MenuItem key={audience.id} value={audience.id}>
                      {audience.name || 'Untitled audience'}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {linkedToSaved ? (
                <Alert
                  severity="info"
                  action={
                    <Button
                      size="small"
                      onClick={handleCustomize}
                      disabled={!isDraft || busy}
                    >
                      Customize
                    </Button>
                  }
                >
                  Rules, handpicked contacts, and exclusions come from the saved audience.
                  Customize to edit them on this campaign only.
                </Alert>
              ) : null}
              <AudienceFilterBuilder
                value={audienceFilter}
                onChange={setAudienceFilter}
                salesOwners={salesOwners}
                inventoryOwners={inventoryOwners}
                terminals={terminals}
                disabled={!isDraft || busy || linkedToSaved}
              />
              <Autocomplete
                multiple
                disabled={!isDraft || busy || linkedToSaved}
                options={contacts.filter((contact) => !excludedContactIds.includes(contact.id))}
                value={contactsByIds(contacts, includedContactIds)}
                onChange={(_, next) => setIncludedContactIds(next.map((contact) => contact.id))}
                getOptionLabel={contactOptionLabel}
                isOptionEqualToValue={(left, right) => left.id === right.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index })
                    return (
                      <Chip key={key} {...tagProps} size="small" label={contactOptionLabel(option)} />
                    )
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Handpick contacts" placeholder="Add specific people" />
                )}
              />
              <FormHelperText sx={{ mx: 0 }}>
                With no filter rules, only these people are included. Otherwise they are added on top of the rules.
              </FormHelperText>
              <Autocomplete
                multiple
                disabled={!isDraft || busy || linkedToSaved}
                options={contacts.filter((contact) => !includedContactIds.includes(contact.id))}
                value={contactsByIds(contacts, excludedContactIds)}
                onChange={(_, next) => setExcludedContactIds(next.map((contact) => contact.id))}
                getOptionLabel={contactOptionLabel}
                isOptionEqualToValue={(left, right) => left.id === right.id}
                renderTags={(value, getTagProps) =>
                  value.map((option, index) => {
                    const { key, ...tagProps } = getTagProps({ index })
                    return (
                      <Chip key={key} {...tagProps} size="small" label={contactOptionLabel(option)} />
                    )
                  })
                }
                renderInput={(params) => (
                  <TextField {...params} label="Exclude contacts" placeholder="Leave these people out" />
                )}
              />
              {!linkedToSaved && isDraft ? (
                <>
                  <Divider flexItem />
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                  >
                    <TextField
                      label="Reuse this audience later"
                      placeholder="Name this audience"
                      value={saveAudienceName}
                      onChange={(event) => setSaveAudienceName(event.target.value)}
                      size="small"
                      fullWidth
                      disabled={busy}
                    />
                    <Button
                      onClick={() => void handleSaveAsAudience()}
                      disabled={!saveAudienceName.trim() || busy}
                      sx={{ whiteSpace: 'nowrap' }}
                    >
                      Save audience
                    </Button>
                  </Stack>
                </>
              ) : null}
            </Stack>
          </Paper>

          <Paper
            variant="outlined"
            sx={{
              p: { xs: 1.5, md: 2 },
              borderRadius: 3,
              bgcolor: alpha(theme.palette.background.default, 0.6),
            }}
          >
            <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>
              Recipients
            </Typography>
            <AudienceEligibilitySummary preview={eligibility} />
          </Paper>
        </Stack>
      </DialogContent>

      <DialogActions
        sx={{
          px: { xs: 2, md: 3 },
          py: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          flexWrap: 'wrap',
          gap: 1,
        }}
      >
        <Button onClick={onClose} color="inherit" disabled={busy}>
          Close
        </Button>
        {canReturnToDraft ? (
          <Button
            onClick={() => void handleReturnToDraft()}
            color="inherit"
            startIcon={<Undo />}
            disabled={busy}
          >
            Return to draft
          </Button>
        ) : null}
        {isScheduled || canContinueSend ? (
          <Button
            onClick={() => void handlePause()}
            color="warning"
            startIcon={<Pause />}
            disabled={busy}
          >
            Pause
          </Button>
        ) : null}
        {isPaused ? (
          <Button
            onClick={() => void handleResume()}
            color="warning"
            startIcon={<PlayArrow />}
            disabled={busy}
          >
            Resume
          </Button>
        ) : null}
        {campaign &&
        (isScheduled || canContinueSend || isPaused) ? (
          <Button
            onClick={() => setConfirmCancelOpen(true)}
            color="error"
            disabled={busy}
          >
            Cancel campaign
          </Button>
        ) : null}
        <Box sx={{ flexGrow: 1 }} />
        <Button onClick={handleSave} variant="outlined" disabled={!canSave || !isDraft}>
          {isSaving && !isSending && !isSendingTest && !isUpdatingStatus
            ? 'Saving…'
            : 'Save draft'}
        </Button>
        <Button
          onClick={() => setConfirmTestOpen(true)}
          variant="outlined"
          startIcon={<ForwardToInbox />}
          disabled={!canSendTest}
        >
          {isSendingTest ? 'Sending test…' : 'Send test'}
        </Button>
        {(isDraft || isScheduled) ? (
          <Button
            onClick={() => setConfirmScheduleOpen(true)}
            variant="outlined"
            startIcon={<Schedule />}
            disabled={!canSchedule}
          >
            {isUpdatingStatus && !isSending ? 'Scheduling…' : isScheduled ? 'Update schedule' : 'Schedule'}
          </Button>
        ) : null}
        <Button
          onClick={() => setConfirmSendOpen(true)}
          variant="contained"
          startIcon={<Send />}
          disabled={!canSendNow}
        >
          {isSending ? 'Sending…' : canContinueSend ? 'Continue sending' : 'Send now'}
        </Button>
      </DialogActions>

      <Dialog
        open={confirmTestOpen}
        onClose={busy ? undefined : () => setConfirmTestOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <ForwardToInbox color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Send a test email?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            This sends one copy to <strong>{testToEmail}</strong> with{' '}
            <strong>[TEST]</strong> in the subject. Campaign recipients are not emailed
            and the campaign status does not change.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setConfirmTestOpen(false)}
            color="inherit"
            disabled={busy}
          >
            Cancel
          </Button>
          <Button
            onClick={() => void handleSendTest()}
            variant="contained"
            disabled={busy}
          >
            {isSendingTest ? 'Sending…' : 'Send test'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmSendOpen}
        onClose={busy ? undefined : () => setConfirmSendOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Send color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Send this campaign now?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {isScheduled
              ? `This campaign is scheduled${campaign?.scheduledAt ? ` for ${campaign.scheduledAt.toLocaleString('en-US')}` : ''}. Send now emails ${formatNumber(eligibility.eligibleCount)} eligible recipient${eligibility.eligibleCount === 1 ? '' : 's'} immediately instead of waiting.`
              : `This will email ${formatNumber(eligibility.eligibleCount)} eligible recipient${eligibility.eligibleCount === 1 ? '' : 's'} with the current filters.`}{' '}
            Unsubscribed and suppressed contacts stay excluded. Marketing send does not
            change last sales activity.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button onClick={() => setConfirmSendOpen(false)} color="inherit" disabled={busy}>
            Cancel
          </Button>
          <Button
            onClick={() => void handleSendNow()}
            variant="contained"
            disabled={busy}
          >
            {isSending ? 'Sending…' : 'Send now'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmScheduleOpen}
        onClose={busy ? undefined : () => setConfirmScheduleOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Schedule color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>
            Schedule this campaign?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <strong>{name.trim() || 'Untitled campaign'}</strong> will email{' '}
            {formatNumber(eligibility.eligibleCount)} eligible recipient
            {eligibility.eligibleCount === 1 ? '' : 's'}
            {scheduledAt?.isValid()
              ? ` at ${scheduledAt.toDate().toLocaleString('en-US')}`
              : ''}
            {repeat !== 'none'
              ? `, then repeat ${CAMPAIGN_REPEAT_LABEL[repeat].toLowerCase()}`
              : ''}
            . You can pause or send immediately later.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setConfirmScheduleOpen(false)}
            color="inherit"
            disabled={busy}
          >
            Back
          </Button>
          <Button
            onClick={() => void handleSchedule()}
            variant="contained"
            disabled={busy}
          >
            {isUpdatingStatus ? 'Scheduling…' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={confirmCancelOpen}
        onClose={busy ? undefined : () => setConfirmCancelOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3 } }}
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Typography variant="h6" fontWeight={700}>
            Cancel this campaign?
          </Typography>
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            Cancelled campaigns do not send. This cannot be undone — duplicate the campaign
            if you need a new draft. Emails already delivered cannot be recalled.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 1.5 }}>
          <Button
            onClick={() => setConfirmCancelOpen(false)}
            color="inherit"
            disabled={busy}
          >
            Back
          </Button>
          <Button
            onClick={() => void handleCancelCampaign()}
            color="error"
            variant="contained"
            disabled={busy}
          >
            Cancel campaign
          </Button>
        </DialogActions>
      </Dialog>
    </Dialog>
  )
}

export default CampaignFormDialog
